/**
 * Utility function to detect conflicts between global and plot-specific filters
 * Returns information about whether filters conflict and which filter keys have conflicts
 */
import type { Dayjs } from "dayjs";
import type {
  DateFilterCondition,
  NumericFilterCondition,
} from "@/components/UI/TableToolbar/Filtering/filterTypes";
import {
  DATE_CONDITIONS,
  NUMERIC_CONDITIONS,
} from "@/components/UI/TableToolbar/Filtering/filterTypes";
import { getRelativeDatePresetRange } from "@/components/UI/TableToolbar/Filtering/filtersUtil";

type RangeBounds = {
  min: number | null;
  max: number | null;
  minInclusive: boolean;
  maxInclusive: boolean;
};

type NumericConstraint =
  | {
      // `has_data` is modeled as an unbounded range.
      kind: "range";
      range: RangeBounds;
      excludedValues: Set<number>;
    }
  | {
      kind: "no_data";
    }
  | {
      kind: "empty";
    };

const isDateCondition = (condition: any): condition is DateFilterCondition =>
  typeof condition === "object" &&
  condition !== null &&
  typeof condition.condition === "string" &&
  DATE_CONDITIONS.includes(condition.condition);

const isNumericCondition = (
  condition: any,
): condition is NumericFilterCondition =>
  typeof condition === "object" &&
  condition !== null &&
  typeof condition.condition === "string" &&
  NUMERIC_CONDITIONS.includes(condition.condition);

const toTimestamp = (value: string | Date | Dayjs | undefined) => {
  if (!value) {
    return null;
  }

  const timestamp =
    typeof (value as Dayjs).toDate === "function"
      ? (value as Dayjs).toDate().getTime()
      : new Date(value as string | Date).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const getDateRangeForCondition = (
  condition: DateFilterCondition,
): RangeBounds | null => {
  switch (condition.condition) {
    case "equals": {
      const timestamp = toTimestamp(condition.value);
      return timestamp === null
        ? null
        : {
            min: timestamp,
            max: timestamp,
            minInclusive: true,
            maxInclusive: true,
          };
    }
    case "before": {
      const timestamp = toTimestamp(condition.value);
      return timestamp === null
        ? null
        : {
            min: null,
            max: timestamp,
            minInclusive: true,
            maxInclusive: false,
          };
    }
    case "after": {
      const timestamp = toTimestamp(condition.value);
      return timestamp === null
        ? null
        : {
            min: timestamp,
            max: null,
            minInclusive: false,
            maxInclusive: true,
          };
    }
    case "between": {
      let startDate = condition.startDate;
      let endDate = condition.endDate;

      if (condition.mode === "relative" && condition.relativePreset) {
        const [resolvedStart, resolvedEnd] = getRelativeDatePresetRange(
          condition.relativePreset,
        );
        startDate = resolvedStart;
        endDate = resolvedEnd;
      }

      const startTimestamp = toTimestamp(startDate);
      const endTimestamp = toTimestamp(endDate);

      if (startTimestamp === null || endTimestamp === null) {
        return null;
      }

      return {
        min: startTimestamp,
        max: endTimestamp,
        minInclusive: true,
        maxInclusive: true,
      };
    }
    default:
      return null;
  }
};

const getNumericRangeForCondition = (
  condition: NumericFilterCondition,
): RangeBounds | null => {
  switch (condition.condition) {
    case "equals":
      return condition.value === undefined
        ? null
        : {
            min: condition.value,
            max: condition.value,
            minInclusive: true,
            maxInclusive: true,
          };
    case "greater_than":
      return condition.value === undefined
        ? null
        : {
            min: condition.value,
            max: null,
            minInclusive: false,
            maxInclusive: true,
          };
    case "greater_than_or_equal":
      return condition.value === undefined
        ? null
        : {
            min: condition.value,
            max: null,
            minInclusive: true,
            maxInclusive: true,
          };
    case "less_than":
      return condition.value === undefined
        ? null
        : {
            min: null,
            max: condition.value,
            minInclusive: true,
            maxInclusive: false,
          };
    case "less_than_or_equal":
      return condition.value === undefined
        ? null
        : {
            min: null,
            max: condition.value,
            minInclusive: true,
            maxInclusive: true,
          };
    case "between":
      return condition.min === undefined || condition.max === undefined
        ? null
        : {
            min: condition.min,
            max: condition.max,
            minInclusive: true,
            maxInclusive: true,
          };
    case "has_data":
      return {
        min: null,
        max: null,
        minInclusive: true,
        maxInclusive: true,
      };
    default:
      return null;
  }
};

const intersectRanges = (first: RangeBounds, second: RangeBounds) => {
  let min = first.min;
  let minInclusive = first.minInclusive;

  if (
    second.min !== null &&
    (min === null ||
      second.min > min ||
      (second.min === min && !second.minInclusive))
  ) {
    min = second.min;
    minInclusive = second.minInclusive;
  } else if (second.min !== null && min !== null && second.min === min) {
    minInclusive = first.minInclusive && second.minInclusive;
  }

  let max = first.max;
  let maxInclusive = first.maxInclusive;

  if (
    second.max !== null &&
    (max === null ||
      second.max < max ||
      (second.max === max && !second.maxInclusive))
  ) {
    max = second.max;
    maxInclusive = second.maxInclusive;
  } else if (second.max !== null && max !== null && second.max === max) {
    maxInclusive = first.maxInclusive && second.maxInclusive;
  }

  if (min !== null && max !== null) {
    if (min > max) {
      return null;
    }

    if (min === max && (!minInclusive || !maxInclusive)) {
      return null;
    }
  }

  return { min, max, minInclusive, maxInclusive };
};

const getCombinedDateRange = (values: any[]): RangeBounds | null => {
  if (values.length === 0 || !values.every(isDateCondition)) {
    return null;
  }

  let combinedRange: RangeBounds = {
    min: null,
    max: null,
    minInclusive: true,
    maxInclusive: true,
  };

  for (const value of values) {
    const range = getDateRangeForCondition(value);

    if (!range) {
      return null;
    }

    const intersection = intersectRanges(combinedRange, range);
    if (!intersection) {
      return null;
    }

    combinedRange = intersection;
  }

  return combinedRange;
};

const rangesOverlap = (first: RangeBounds, second: RangeBounds) => {
  const intersection = intersectRanges(first, second);
  return intersection !== null;
};

const rangeContainsValue = (range: RangeBounds, value: number) => {
  if (range.min !== null) {
    if (value < range.min) {
      return false;
    }

    if (value === range.min && !range.minInclusive) {
      return false;
    }
  }

  if (range.max !== null) {
    if (value > range.max) {
      return false;
    }

    if (value === range.max && !range.maxInclusive) {
      return false;
    }
  }

  return true;
};

const getCombinedNumericConstraint = (
  values: any[],
): NumericConstraint | null => {
  if (values.length === 0 || !values.every(isNumericCondition)) {
    return null;
  }

  const numericValues = values;

  const hasNoData = numericValues.some(
    (value) => value.condition === "no_data",
  );
  const hasOtherConditions = numericValues.some(
    (value) => value.condition !== "no_data",
  );

  if (hasNoData) {
    return hasOtherConditions ? { kind: "empty" } : { kind: "no_data" };
  }

  let combinedRange: RangeBounds = {
    min: null,
    max: null,
    minInclusive: true,
    maxInclusive: true,
  };
  const excludedValues = new Set<number>();

  for (const value of numericValues) {
    if (value.condition === "not_equals") {
      if (value.value !== undefined) {
        excludedValues.add(value.value);
      }
      continue;
    }

    const range = getNumericRangeForCondition(value);

    if (!range) {
      return null;
    }

    const intersection = intersectRanges(combinedRange, range);
    if (!intersection) {
      return null;
    }

    combinedRange = intersection;
  }

  if (
    combinedRange.min !== null &&
    combinedRange.max !== null &&
    combinedRange.min === combinedRange.max &&
    combinedRange.minInclusive &&
    combinedRange.maxInclusive &&
    excludedValues.has(combinedRange.min)
  ) {
    return { kind: "empty" };
  }

  return { kind: "range", range: combinedRange, excludedValues };
};

const numericConstraintsOverlap = (
  first: NumericConstraint,
  second: NumericConstraint,
) => {
  if (first.kind === "empty" || second.kind === "empty") {
    return false;
  }

  if (first.kind === "no_data" || second.kind === "no_data") {
    return first.kind === second.kind;
  }

  const intersection = intersectRanges(first.range, second.range);
  if (!intersection) {
    return false;
  }

  if (
    intersection.min !== null &&
    intersection.max !== null &&
    intersection.min === intersection.max &&
    intersection.minInclusive &&
    intersection.maxInclusive
  ) {
    const point = intersection.min;
    return (
      !first.excludedValues.has(point) &&
      !second.excludedValues.has(point) &&
      rangeContainsValue(first.range, point) &&
      rangeContainsValue(second.range, point)
    );
  }

  return true;
};

export const detectFilterConflicts = (globalFilters: any, plotFilters: any) => {
  if (!globalFilters || !plotFilters || Object.keys(plotFilters).length === 0) {
    return { hasConflicts: false, conflicts: [] };
  }

  const conflicts: string[] = [];

  Object.keys(plotFilters).forEach((key) => {
    if (globalFilters[key]) {
      const globalValues = Array.isArray(globalFilters[key])
        ? globalFilters[key]
        : [globalFilters[key]];
      const plotValues = Array.isArray(plotFilters[key])
        ? plotFilters[key]
        : [plotFilters[key]];

      // Only check for conflicts if both global and plot filters have values
      if (globalValues.length > 0 && plotValues.length > 0) {
        const globalDateRange = getCombinedDateRange(globalValues);
        const plotDateRange = getCombinedDateRange(plotValues);

        if (globalDateRange && plotDateRange) {
          if (!rangesOverlap(globalDateRange, plotDateRange)) {
            conflicts.push(key);
          }
          return;
        }

        const globalNumericConstraint =
          getCombinedNumericConstraint(globalValues);
        const plotNumericConstraint = getCombinedNumericConstraint(plotValues);

        if (globalNumericConstraint && plotNumericConstraint) {
          if (
            !numericConstraintsOverlap(
              globalNumericConstraint,
              plotNumericConstraint,
            )
          ) {
            conflicts.push(key);
          }
          return;
        }

        // Find intersection
        const intersection = globalValues.filter((value) =>
          plotValues.includes(value),
        );

        if (intersection.length === 0) {
          conflicts.push(key);
        }
      }
    }
  });

  return { hasConflicts: conflicts.length > 0, conflicts };
};
